import React, { useEffect } from 'react';
import { useForm, Controller, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import styles from "./styles.module.scss";
import { CustomSelect, Input, OptionType } from 'components/form';
import { BaseEditModal } from '../base-edit-modal';
import { BasicExpectationsEditPayload } from 'hooks/criteria-modal-hooks/useBasicExpectations';
import * as yup from 'yup';
import { normalizeWorkMode, shouldCollectJobLocation, WORK_MODE_OPTIONS } from 'utils/workMode';

export interface BasicExpectationFormData {
  employmentType?: string;
  isEmploymentType: boolean;
  experienceLevel?: string;
  isExperienceLevel: boolean;
  yearsOfExperience?: string;
  isYearsOfExperience: boolean;
  workMode?: string;
  jobLocation?: string;
  isWorkMode: boolean;
  workEnvironment?: string;
  isWorkEnvironment: boolean;
  minSalary?: string;
  maxSalary?: string;
  salaryCurrency?: string;
  salaryPeriod?: string;
  isSalaryRange: boolean;
  displaySalary?: boolean;
  salaryRange?: string;
  isCV: boolean;
  isPortfolio: boolean;
}

interface BasicExpectationEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentData: BasicExpectationFormData;
  onSave: (data: BasicExpectationFormData) => void;
  isSubmitting?: boolean;
  showToggles?: boolean;
}

export const employmentTypeOptions = [
  { value: 'fulltime', label: 'Full-time' },
  { value: 'parttime', label: 'Part-time' },
  { value: 'contract', label: 'Contract' },
  { value: 'internship', label: 'Internship' }
];

export const workModeOptions: OptionType[] = WORK_MODE_OPTIONS;

export const experienceLevelOptions = [
  { value: 'entry', label: 'Entry Level' },
  { value: 'associate', label: 'Associate' },
  { value: 'mid', label: 'Mid Level' },
  { value: 'senior', label: 'Senior/Executive' }
];

export const yearsOfExperienceOptions = [
  { value: '0_1', label: '0-1 years' },
  { value: '1_3', label: '1-3 years' },
  { value: '3_5', label: '3-5 years' },
  { value: '6_8', label: '6-8 years' },
  { value: '8_10', label: '8-10 years' },
  { value: 'OVER_10', label: '10+ years' }
];

export const workEnvironmentOptions: OptionType[] = [
  { value: 'startup', label: 'Startup' },
  { value: 'growing_business', label: 'Growing Business' },
  { value: 'established_organisation', label: 'Established Organisation' },
  { value: 'large_corporations', label: 'Large Corporations' },
  { value: 'freelance', label: 'Freelance' }
];

export const currencyOptions = [
  { value: 'USD', label: 'USD' },
  { value: 'NGN', label: 'NGN' },
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' }
];

/**
 * Build a minimal PATCH payload for basic expectations: only the fields that differ
 * between `original` and `next` are included (always alongside `assessment_id`). Because
 * the endpoint is a PATCH, omitted fields are left untouched server-side — so we send
 * just what the user actually edited instead of the whole form every time.
 *
 * If `original` is missing we can't diff, so every field is included (full update).
 */
export const buildBasicExpectationPatch = (
  next: BasicExpectationFormData,
  original: BasicExpectationFormData | undefined,
  assessmentId: string,
): BasicExpectationsEditPayload => {
  const payload: BasicExpectationsEditPayload = { assessment_id: assessmentId };
  const o = original;

  const text = (v?: string) => (v ?? '').trim();
  const amount = (v?: string) => parseInt(v ?? '0', 10) || 0;

  if (!o || text(next.employmentType) !== text(o.employmentType)) payload.employment_type = next.employmentType || '';
  if (!o || next.isEmploymentType !== o.isEmploymentType) payload.require_employment_type = next.isEmploymentType;

  if (!o || text(next.experienceLevel) !== text(o.experienceLevel)) payload.experience_level = next.experienceLevel || '';
  if (!o || next.isExperienceLevel !== o.isExperienceLevel) payload.require_experience_level = next.isExperienceLevel;

  if (!o || text(next.yearsOfExperience) !== text(o.yearsOfExperience)) payload.years_of_experience = next.yearsOfExperience || '';
  if (!o || next.isYearsOfExperience !== o.isYearsOfExperience) payload.require_years_of_experience = next.isYearsOfExperience;

  const nextWorkMode = normalizeWorkMode(next.workMode);
  const originalWorkMode = normalizeWorkMode(o?.workMode);
  if (!o || nextWorkMode !== originalWorkMode) payload.work_mode = nextWorkMode;
  if (!o || next.isWorkMode !== o.isWorkMode) payload.require_work_mode = next.isWorkMode;

  // A remote role has no physical job location. Explicitly clear a previously
  // saved address when switching to remote; onsite/hybrid edits send the text.
  const nextAddress = nextWorkMode === 'remote' ? null : text(next.jobLocation);
  const originalAddress = originalWorkMode === 'remote' ? null : text(o?.jobLocation);
  if (!o || nextAddress !== originalAddress) payload.address = nextAddress;

  if (!o || text(next.workEnvironment) !== text(o.workEnvironment)) payload.experience_environment = next.workEnvironment || '';
  if (!o || next.isWorkEnvironment !== o.isWorkEnvironment) payload.require_work_environment = next.isWorkEnvironment;

  if (!o || amount(next.minSalary) !== amount(o.minSalary)) payload.min_salary_range_amount = amount(next.minSalary);
  if (!o || amount(next.maxSalary) !== amount(o.maxSalary)) payload.max_salary_range_amount = amount(next.maxSalary);
  if (!o || text(next.salaryCurrency).toUpperCase() !== text(o.salaryCurrency).toUpperCase()) payload.salary_range_currency = (next.salaryCurrency ?? '').toUpperCase();
  if (!o || text(next.salaryPeriod) !== text(o.salaryPeriod)) payload.salary_range_term = next.salaryPeriod || '';
  if (!o || next.isSalaryRange !== o.isSalaryRange) payload.require_salary_range = next.isSalaryRange;

  if (!o || (next.displaySalary ?? false) !== (o.displaySalary ?? false)) payload.display_salary = next.displaySalary ?? false;
  if (!o || next.isCV !== o.isCV) payload.require_cv_upload = next.isCV;
  if (!o || next.isPortfolio !== o.isPortfolio) payload.require_portfolio_upload = next.isPortfolio;

  return payload;
};

/** Whether a patch payload carries any actual change (beyond the required assessment_id). */
export const hasBasicExpectationChanges = (payload: BasicExpectationsEditPayload): boolean =>
  Object.keys(payload).some((key) => key !== 'assessment_id');

export const getSelectedOption = (options: any[], value: string | undefined) => {
  return options.find(option => option.value === value) || { value: '', label: '' };
};

export const getSelectedOptions = (options: any[], values: string[] | undefined) => {
  return options.filter(option => values?.includes(option.value));
};

// Create a more flexible schema type
const basicExpectationSchema: yup.ObjectSchema<any> = yup.object().shape({
  isEmploymentType: yup.boolean().required(),
  employmentType: yup.string().when('isEmploymentType', {
    is: true,
    then: (schema) => schema.required('Employment type is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  isExperienceLevel: yup.boolean().required(),
  experienceLevel: yup.string().when('isExperienceLevel', {
    is: true,
    then: (schema) => schema.required('Experience level is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  isYearsOfExperience: yup.boolean().required(),
  yearsOfExperience: yup.string().when('isYearsOfExperience', {
    is: true,
    then: (schema) => schema.required('Years of experience is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  isWorkMode: yup.boolean().required(),
  workMode: yup.string().when('isWorkMode', {
    is: true,
    then: (schema) => schema.required('Work mode is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  jobLocation: yup.string().when(['isWorkMode', 'workMode'], {
    is: (isWorkMode: boolean, workMode: string) => isWorkMode && shouldCollectJobLocation(workMode),
    then: (schema) => schema.trim().required('Job location is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  isWorkEnvironment: yup.boolean().required(),
  workEnvironment: yup.string().when('isWorkEnvironment', {
    is: true,
    then: (schema) => schema.required('Work environment is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  isSalaryRange: yup.boolean().required(),
  minSalary: yup.string().when('isSalaryRange', {
    is: true,
    then: (schema) => schema.required('Minimum salary is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  maxSalary: yup.string().when('isSalaryRange', {
    is: true,
    then: (schema) =>
      schema
        .required('Maximum salary is required')
        .test('is-greater', 'Maximum salary must be greater than minimum salary', function (value) {
          const { minSalary } = this.parent;
          return Number(value) > Number(minSalary);
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  salaryCurrency: yup.string().when('isSalaryRange', {
    is: true,
    then: (schema) => schema.required('Currency is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  salaryPeriod: yup.string().when('isSalaryRange', {
    is: true,
    then: (schema) => schema.required('Salary period is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  isCV: yup.boolean().required(),
  isPortfolio: yup.boolean().required(),
});

export const BasicExpectationEditModal: React.FC<BasicExpectationEditModalProps> = ({
  isOpen,
  onClose,
  currentData,
  onSave,
  isSubmitting,
  showToggles = true,
}) => {
  const canShowToggles = showToggles;
  const defaultValues: BasicExpectationFormData = currentData || {
    employmentType: undefined,
    isEmploymentType: true,
    experienceLevel: undefined,
    isExperienceLevel: true,
    yearsOfExperience: undefined,
    isYearsOfExperience: true,
    workMode: undefined,
    jobLocation: undefined,
    isWorkMode: true,
    workEnvironment: undefined,
    isWorkEnvironment: true,
    minSalary: undefined,
    maxSalary: undefined,
    salaryCurrency: undefined,
    salaryPeriod: undefined,
    isSalaryRange: true,
    displaySalary: false,
    isCV: true,
    isPortfolio: true,
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset,
    register
  } = useForm<BasicExpectationFormData>({
    defaultValues,
    resolver: yupResolver(basicExpectationSchema) as Resolver<BasicExpectationFormData>
  });

  // Re-sync the form to the latest saved data every time the modal opens. Without
  // the isOpen guard, a Cancel would leave the edited (but unsaved) values in the
  // form, so reopening showed stale changes that were never persisted.
  useEffect(() => {
    if (isOpen) {
      reset(currentData || defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentData, reset]);

  const onSubmit = (data: BasicExpectationFormData) => {
    onSave(data);
  };

  const salaryPeriodOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Annually' }
  ];

  return (
    <BaseEditModal
      isOpen={isOpen}
      onClose={onClose}
      title="Basic Expectations"
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
    >
      {canShowToggles && (
        <p className={styles.toggleHint}>
          Toggle a field on to make it visible and required for candidates. Toggle it off to hide it from the candidate's application form.
        </p>
      )}
      <div className={styles.formSection}>
        <div className={`${styles.inputRow}`}>
          <div className={`${canShowToggles && !watch('isEmploymentType') ? styles.disabled : ''}`}>
            <Controller
              name="employmentType"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  label="Employment type:"
                  placeholder="Select employment type"
                  options={employmentTypeOptions}
                  value={getSelectedOption(employmentTypeOptions, field.value)}
                  onChange={(option) => field.onChange(option?.value || undefined)}
                  validatorMessage={errors.employmentType?.message || ''}
                  name="employmentType"
                  parentClassName={styles.selectField}
                  inputClass={styles.selectInput}
                  disabled={canShowToggles ? !watch('isEmploymentType') : false}
                />
              )}
            />
          </div>
          {canShowToggles && (
            <div className={styles.toggleLabel}>
              <label className={styles.toggleWrapper}>
                <input
                  type="checkbox"
                  {...register('isEmploymentType')}
                  className={styles.toggleInput}
                  id="isEmploymentType"
                />
                <span className={`${styles.toggleSlider} ${watch('isEmploymentType') ? styles.active : ''}`}>
                  <span className={styles.toggleButton}></span>
                </span>
              </label>
            </div>
          )}
        </div>

        <div className={`${styles.inputRow}`}>
          <div className={`${canShowToggles && !watch('isExperienceLevel') ? styles.disabled : ''}`}>
            <Controller
              name="experienceLevel"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  label="Experience level:"
                  placeholder="Select experience level"
                  options={experienceLevelOptions}
                  value={getSelectedOption(experienceLevelOptions, field.value)}
                  onChange={(option) => field.onChange(option?.value || undefined)}
                  validatorMessage={errors.experienceLevel?.message || ''}
                  name="experienceLevel"
                  parentClassName={styles.selectField}
                  inputClass={styles.selectInput}
                  disabled={canShowToggles ? !watch('isExperienceLevel') : false}
                />
              )}
            />
          </div>

          {canShowToggles && (
            <div className={styles.toggleLabel}>
              <label className={styles.toggleWrapper}>
                <input
                  type="checkbox"
                  {...register('isExperienceLevel')}
                  className={styles.toggleInput}
                  id="isExperienceLevel"
                />
                <span className={`${styles.toggleSlider} ${watch('isExperienceLevel') ? styles.active : ''}`}>
                  <span className={styles.toggleButton}></span>
                </span>
              </label>
            </div>
          )}
        </div>

        <div className={`${styles.inputRow}`}>
          <div className={`${canShowToggles && !watch('isYearsOfExperience') ? styles.disabled : ''}`}>
            <Controller
              name="yearsOfExperience"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  label="Years of experience:"
                  placeholder="Select years of experience"
                  options={yearsOfExperienceOptions}
                  value={getSelectedOption(yearsOfExperienceOptions, field.value)}
                  onChange={(option) => field.onChange(option?.value || undefined)}
                  validatorMessage={errors.yearsOfExperience?.message || ''}
                  name="yearsOfExperience"
                  parentClassName={styles.selectField}
                  inputClass={styles.selectInput}
                  disabled={canShowToggles ? !watch('isYearsOfExperience') : false}
                />
              )}
            />
          </div>

          {canShowToggles && (
            <div className={styles.toggleLabel}>
              <label className={styles.toggleWrapper}>
                <input
                  type="checkbox"
                  {...register('isYearsOfExperience')}
                  className={styles.toggleInput}
                  id="isYearsOfExperience"
                />
                <span className={`${styles.toggleSlider} ${watch('isYearsOfExperience') ? styles.active : ''}`}>
                  <span className={styles.toggleButton}></span>
                </span>
              </label>
            </div>
          )}
        </div>

        <div>
          <div className={`${styles.inputRow}`}>
            <div className={`${styles.workModeFields} ${canShowToggles && !watch('isWorkMode') ? styles.disabled : ''}`}>
              <Controller
                name="workMode"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    label="Work mode:"
                    placeholder="Select work mode"
                    options={workModeOptions}
                    value={getSelectedOption(workModeOptions, normalizeWorkMode(field.value))}
                    onChange={(option) => field.onChange(option?.value || undefined)}
                    validatorMessage={errors.workMode?.message || ''}
                    name="workMode"
                    parentClassName={styles.selectField}
                    inputClass={styles.selectInput}
                    disabled={canShowToggles ? !watch('isWorkMode') : false}
                  />
                )}
              />
              {watch('isWorkMode') && shouldCollectJobLocation(watch('workMode')) && (
                <Input
                  label="Job location:"
                  labelClassName={styles.locationLabel}
                  name="jobLocation"
                  placeholder="Enter address"
                  register={register}
                  validatorMessage={errors.jobLocation?.message}
                  className={styles.locationInput}
                  parentClassName={styles.locationField}
                />
              )}
            </div>

            {canShowToggles && (
              <div className={styles.toggleLabel}>
                <label className={styles.toggleWrapper}>
                  <input
                    type="checkbox"
                    {...register('isWorkMode')}
                    className={styles.toggleInput}
                    id="isWorkMode"
                  />
                  <span className={`${styles.toggleSlider} ${watch('isWorkMode') ? styles.active : ''}`}>
                    <span className={styles.toggleButton}></span>
                  </span>
                </label>
              </div>
            )}
          </div>
        </div>

        <div>
          <div className={`${styles.inputRow}`}>
            <div className={`${canShowToggles && !watch('isWorkEnvironment') ? styles.disabled : ''}`}>
              <Controller
                name="workEnvironment"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    label="Work environment preference:"
                    placeholder="Select work environment"
                    options={workEnvironmentOptions}
                    value={getSelectedOption(workEnvironmentOptions, field.value)}
                    onChange={(option) => field.onChange(option?.value || undefined)}
                    validatorMessage=""
                    name="workEnvironment"
                    parentClassName={styles.selectField}
                    inputClass={styles.selectInput}
                    disabled={canShowToggles ? !watch('isWorkEnvironment') : false}
                  />
                )}
              />
            </div>

            {canShowToggles && (
              <div className={styles.toggleLabel}>
                <label className={styles.toggleWrapper}>
                  <input
                    type="checkbox"
                    {...register('isWorkEnvironment')}
                    className={styles.toggleInput}
                    id="isWorkEnvironment"
                  />
                  <span className={`${styles.toggleSlider} ${watch('isWorkEnvironment') ? styles.active : ''}`}>
                    <span className={styles.toggleButton}></span>
                  </span>
                </label>
              </div>
            )}
          </div>
          {errors.workEnvironment?.message && (
            <small className={styles.message}>{errors.workEnvironment.message}</small>
          )}
        </div>

        <div className={`${styles.inputRow}`}>
          <div className={`${canShowToggles && !watch('isSalaryRange') ? styles.disabled : ''}`}>
            <div className={styles.salaryRange}>
              <label className={styles.salaryLabel}>Salary range:</label>
              <div className={styles.salaryControls}>
                <Controller
                  name="salaryCurrency"
                  control={control}
                  defaultValue="usd"
                  render={({ field }) => (
                    <CustomSelect
                      placeholder="USD"
                      options={currencyOptions}
                      value={getSelectedOption(currencyOptions, field.value || 'usd')}
                      onChange={(option) => field.onChange(option?.value || undefined)}
                      validatorMessage=""
                      name="salaryCurrency"
                      parentClassName={styles.currencySelect}
                      inputClass={`${styles.salaryInput} ${styles.currencyInput}`}
                      disabled={canShowToggles ? !watch('isSalaryRange') : false}
                    />
                  )}
                />
                <div className={styles.rangeInputs}>
                  <Controller
                    name="minSalary"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        name="minSalary"
                        register={register}
                        placeholder="Enter min amount"
                        className={`${styles.rangeInput} ${errors.minSalary ? styles.inputError : ''}`}
                        validatorMessage=""
                        parentClassName={styles.salaryInput}
                        disabled={canShowToggles ? !watch('isSalaryRange') : false}
                      />
                    )}
                  />
                  <span className={styles.separator}>-</span>
                  <Controller
                    name="maxSalary"
                    control={control}
                    render={({ field }) => (
                      <Input
                        type="number"
                        name="maxSalary"
                        register={register}
                        placeholder="Enter max amount"
                        className={`${styles.rangeInput} ${errors.maxSalary ? styles.inputError : ''}`}
                        validatorMessage=""
                        parentClassName={styles.salaryInput}
                        disabled={canShowToggles ? !watch('isSalaryRange') : false}
                      />
                    )}
                  />
                </div>
                <Controller
                  name="salaryPeriod"
                  control={control}
                  defaultValue="monthly"
                  render={({ field }) => (
                    <CustomSelect
                      placeholder="Monthly"
                      options={salaryPeriodOptions}
                      value={getSelectedOption(salaryPeriodOptions, field.value || 'monthly')}
                      onChange={(option) => field.onChange(option?.value || undefined)}
                      validatorMessage=""
                      name="salaryPeriod"
                      parentClassName={styles.periodSelect}
                      inputClass={styles.selectInput}
                      disabled={canShowToggles ? !watch('isSalaryRange') : false}
                    />
                  )}
                />
              </div>
            </div>
            {(errors.minSalary || errors.maxSalary) && (
              <small className={styles.message}>
                {errors.minSalary?.message || errors.maxSalary?.message}
              </small>
            )}
          </div>

          {canShowToggles && (
            <div className={styles.toggleLabel}>
              <label className={styles.toggleWrapper}>
                <input
                  type="checkbox"
                  {...register('isSalaryRange')}
                  className={styles.toggleInput}
                  id="isSalaryRange"
                />
                <span className={`${styles.toggleSlider} ${watch('isSalaryRange') ? styles.active : ''}`}>
                  <span className={styles.toggleButton}></span>
                </span>
              </label>
            </div>
          )}
        </div>

        {canShowToggles && (<div className={`${styles.inputRow}`}>
          <div className={`${canShowToggles && !watch('isCV') ? styles.disabled : ''}`}>
            <p>CV Upload</p>
          </div>

          <div className={styles.toggleLabel}>
            <label className={styles.toggleWrapper}>
              <input
                type="checkbox"
                {...register('isCV')}
                className={styles.toggleInput}
                id="isCV"
              />
              <span className={`${styles.toggleSlider} ${watch('isCV') ? styles.active : ''}`}>
                <span className={styles.toggleButton}></span>
              </span>
            </label>
          </div>
        </div>)}

        {canShowToggles && (<div className={`${styles.inputRow}`}>
          <div className={`${canShowToggles && !watch('isPortfolio') ? styles.disabled : ''}`}>
            <p>Portfolio Link</p>
          </div>

          <div className={styles.toggleLabel}>
            <label className={styles.toggleWrapper}>
              <input
                type="checkbox"
                {...register('isPortfolio')}
                className={styles.toggleInput}
                id="isPortfolio"
              />
              <span className={`${styles.toggleSlider} ${watch('isPortfolio') ? styles.active : ''}`}>
                <span className={styles.toggleButton}></span>
              </span>
            </label>
          </div>
        </div>)}
      </div>
    </BaseEditModal>
  );
};
